import {
  ReactFlow,
  useStoreApi,
  PanOnScrollMode,
  useKeyPress,
  NodeChange,
  applyNodeChanges,
  useReactFlow,
  useNodesInitialized,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ActionType,
  Connection,
  FlowOperationType,
  flowStructureUtil,
  FlowVersion,
  Step,
  StepLocationRelativeToParent,
} from '@activepieces/shared';

import {
  doesSelectionRectangleExist,
  NODE_SELECTION_RECT_CLASS_NAME,
  useBuilderStateContext,
  useFocusedFailedStep,
  useHandleKeyPressOnCanvas,
  usePasteActionsInClipboard,
} from '../builder-hooks';

import {
  CanvasContextMenu,
  ContextMenuType,
} from './context-menu/canvas-context-menu';
import {
  BIG_ADD_BUTTON_NODE_SUFFIX,
  FLOW_END_NODE_SUFFIX,
  flowUtilConsts,
  LOOP_RETURN_NODE_SUFFIX,
  NEW_STEP_NODE_SUFFIX,
  SELECTION_RECT_CHEVRON_ATTRIBUTE,
  STEP_CONTEXT_MENU_ATTRIBUTE,
} from './utils/consts';
import { flowCanvasUtils } from './utils/flow-canvas-utils';
import {
  ApBigAddButtonNode,
  ApEdge,
  ApLoopReturnNode,
  ApNewStepNode,
  ApNode,
  ApNodeType,
  ApStepNode,
} from './utils/types';
import { useShowChevronNextToSelection } from './widgets/selection-chevron-button';


// Let the DOM settle after a graph re-render before refitting the viewport
const CANVAS_REFIT_DELAY_MS = 150;
const createGraphKey = (flowVersion: FlowVersion) => {
  const allSteps = flowStructureUtil.getAllSteps(flowVersion);
  const stepsKey = allSteps
    .map((step) => {
      const nextName = step.nextAction?.name || '';
      let extra = '';
      if (step.type === ActionType.ROUTER) {
        extra = Array.isArray(step.settings.branches)
          ? step.settings.branches
              .map((branch) => branch.branchName)
              .join('-')
          : '';
      }
      return `${step.name}-${step.displayName}-${step.type}-${nextName}-${extra}`;
    })
    .join('|');

  function getConnectionsKey(
    flow: FlowVersion | { connections?: Connection[]; actions?: Step[] }
  ): string {
    let key = '';
    if (flow.connections && flow.connections.length > 0) {
      key += flow.connections
        .map((conn) => `${conn.sourceId}-${conn.targetId}`)
        .join('|');
    }
    const actions = flow.actions ?? [];
    if (actions.length > 0) {
      for (const action of actions) {
        if (action.type === ActionType.LOOP_ON_ITEMS && action.subflow) {
          key += '|' + getConnectionsKey(action.subflow);
        } else if (
          action.type === ActionType.ROUTER &&
          Array.isArray(action.children)
        ) {
          for (const child of action.children) {
            if (child) {
              key += '|' + getConnectionsKey(child);
            }
          }
        }
      }
    }
    return key;
  }

  const connectionsKey = getConnectionsKey(flowVersion);
  return `${stepsKey}-${connectionsKey}`;
};

export const FlowCanvas = React.memo(() => {
  const [
    flowVersion,
    readonly,
    setSelectedNodes,
    selectedNodes,
    applyOperation,
    selectedStep,
    exitStepSettings,
    panningMode,
    setPieceSelectorStep,
    selectStepByName,
  ] = useBuilderStateContext((state) => {
    return [
      state.flowVersion,
      state.readonly,
      state.setSelectedNodes,
      state.selectedNodes,
      state.applyOperation,
      state.selectedStep,
      state.exitStepSettings,
      state.panningMode,
      state.setPieceSelectorStep,
      state.selectStepByName,
    ];
  });
  const [autoLayoutFlag, resetAutoLayout, canvasRefitFlag, resetCanvasRefit] =
    useBuilderStateContext((state) => [
      state.autoLayoutFlag,
      state.resetAutoLayout,
      state.canvasRefitFlag,
      state.resetCanvasRefit,
    ]);
  const alignAllNodes = flowCanvasUtils.useResetNodesPosition();
  const containerRef = useRef<HTMLDivElement>(null);
  const { actionsToPaste, fetchClipboardOperations } =
    usePasteActionsInClipboard();
  useShowChevronNextToSelection();
  useFocusedFailedStep();
  useHandleKeyPressOnCanvas();
  const storeApi = useStoreApi();
  const isShiftKeyPressed = useKeyPress('Shift');
  const inGrabPanningMode = !isShiftKeyPressed && panningMode === 'grab';

  const graphKey = createGraphKey(flowVersion);
  const graph = useMemo(() => {
    return flowCanvasUtils.convertFlowVersionToGraph(flowVersion);
  }, [flowVersion, graphKey]);

  const flowStorageKey = `flow-positions-${flowVersion.id}`;
  const [forceReloadFromGraph, setForceReloadFromGraph] = useState(false);
  const nodesInitialized = useNodesInitialized();
  const hasFittedView = useRef(false);

  const loadStoredPositions = useCallback((): Record<
    string,
    { x: number; y: number }
  > | null => {
    try {
      const stored = localStorage.getItem(flowStorageKey);
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      console.error('Failed to load positions from localStorage', e);
      return null;
    }
  }, [flowStorageKey]);

  const savePositions = useCallback(
    (nodesToSave: ApNode[]) => {
      try {
        const positions: Record<string, { x: number; y: number }> = {};
        nodesToSave.forEach((node) => {
          positions[node.id] = node.position;
        });
        localStorage.setItem(flowStorageKey, JSON.stringify(positions));
      } catch (e) {
        console.error('Failed to save positions to localStorage', e);
      }
    },
    [flowStorageKey]
  );

  const applyStoredPositions = useCallback(
    (graphNodes: ApNode[]): ApNode[] => {
      const storedPositions = loadStoredPositions();
      if (!storedPositions) return graphNodes;

      return graphNodes.map((node) => ({
        ...node,
        position: storedPositions[node.id] || node.position,
      }));
    },
    [loadStoredPositions]
  );

  const [nodes, setNodes] = useState(() => applyStoredPositions(graph.nodes));
  const [edges, setEdges] = useState(graph.edges);

  useEffect(() => {
    const handleForceReset = () => setForceReloadFromGraph(true);
    window.addEventListener('flow-canvas-force-reset', handleForceReset);
    return () =>
      window.removeEventListener('flow-canvas-force-reset', handleForceReset);
  }, []);

  useEffect(() => {
    if (forceReloadFromGraph) {
      setNodes(graph.nodes);
      setEdges(graph.edges);
      setForceReloadFromGraph(false);
    }
  }, [forceReloadFromGraph, graph]);

  useEffect(() => {
    savePositions(nodes);
  }, [nodes, savePositions]);

  const previousGraphKeyRef = useRef(graphKey);
  useEffect(() => {
    if (previousGraphKeyRef.current !== graphKey) {
      setNodes((prevNodes) => {
        const prevNodesMap = new Map(prevNodes.map((n) => [n.id, n]));

        return graph.nodes.map((newNode) => {
          const existingNode = prevNodesMap.get(newNode.id);
          return existingNode
            ? { ...newNode, position: existingNode.position }
            : newNode;
        });
      });

      setEdges(graph.edges);
      previousGraphKeyRef.current = graphKey;
    }
  }, [graphKey, graph]);

  const onNodesChange = useCallback((changes: NodeChange[]) => {
    setNodes((nds: ApNode[]) => applyNodeChanges(changes, nds) as ApNode[]);
  }, []);

  const { getIntersectingNodes, getNode, fitView } = useReactFlow();

  useEffect(() => {
    if (nodesInitialized && !hasFittedView.current) {
      hasFittedView.current = true;
      fitView();
    }
  }, [nodesInitialized, fitView]);

  // AI builder partial updates: positions are already laid out locally, so only
  // re-render from the flow and refit the viewport (no persisting DRAG_STEP)
  useEffect(() => {
    if (canvasRefitFlag) {
      resetCanvasRefit();
      setForceReloadFromGraph(true);
      setTimeout(() => {
        fitView({ duration: 300 });
      }, CANVAS_REFIT_DELAY_MS);
    }
  }, [canvasRefitFlag, resetCanvasRefit, fitView]);

  // Auto align nodes (persists positions) at the end of an AI builder turn
  useEffect(() => {
    if (autoLayoutFlag) {
      resetAutoLayout();
      alignAllNodes();
      setForceReloadFromGraph(true);
      setTimeout(() => {
        fitView({ duration: 300 });
      }, CANVAS_REFIT_DELAY_MS);
    }
  }, [autoLayoutFlag, alignAllNodes, resetAutoLayout, fitView]);

  const onNodeDragStop = useCallback(
    (event: any, draggedNode: ApNode) => {
      const isSystemNode = (id: string) =>
        [
          FLOW_END_NODE_SUFFIX,
          NEW_STEP_NODE_SUFFIX,
          LOOP_RETURN_NODE_SUFFIX,
          BIG_ADD_BUTTON_NODE_SUFFIX,
        ].some((suffix) => id.endsWith(suffix));

      const handleNewStepNodeDragStop = (node: ApNewStepNode) => {
        const [firstIntersectionId] = getIntersectingNodes(node).map(
          (n) => n.id
        );

        const targetNode = getNode(firstIntersectionId);

        const isTargetValidForAttachment =
          firstIntersectionId &&
          firstIntersectionId !== node.data.parentStepName &&
          !isSystemNode(firstIntersectionId) &&
          !flowStructureUtil.isTrigger((targetNode?.data?.step as Step)?.type);

        if (isTargetValidForAttachment) {
          applyOperation(
            {
              type: FlowOperationType.ATTACH_STEPS,
              request: {
                connection: {
                  sourceId: node.data.parentStepName,
                  targetId: firstIntersectionId,
                },
              },
            },
            () => console.error('Operation failed')
          );
        }

        if (node.id.endsWith(FLOW_END_NODE_SUFFIX)) {
          updateFlowEndNodePosition(node);
        } else if (node.id.endsWith(NEW_STEP_NODE_SUFFIX)) {
          updateNewStepNodePosition(node);
        }
      };

      const handleDragToAttachStep = (node: ApNode): boolean => {
        const firstIntersectionId = getIntersectingNodes(node)[0].id;

        if (
          firstIntersectionId &&
          firstIntersectionId.endsWith(BIG_ADD_BUTTON_NODE_SUFFIX)
        ) {
          const targetBigAddButtonNode = getNode(firstIntersectionId) as
            | ApBigAddButtonNode
            | undefined;
          const draggedStepNode =
            node.type === ApNodeType.STEP ? (node as ApStepNode) : null;

          if (targetBigAddButtonNode && draggedStepNode?.data.step) {
            const { step } = draggedStepNode.data;

            if (flowStructureUtil.isAction(step.type)) {
              const buttonData = targetBigAddButtonNode.data;
              const branchIndex =
                buttonData.stepLocationRelativeToParent ===
                StepLocationRelativeToParent.INSIDE_BRANCH
                  ? buttonData.branchIndex
                  : undefined;

              applyOperation(
                {
                  type: FlowOperationType.MOVE_ACTION,
                  request: {
                    name: step.name,
                    newParentStep: buttonData.parentStepName,
                    stepLocationRelativeToNewParent:
                      buttonData.stepLocationRelativeToParent,
                    branchIndex,
                    skipImportOperations: true,
                    newStepPosition: targetBigAddButtonNode.position,
                  },
                },
                () => console.error('MOVE_ACTION operation failed')
              );

              return true;
            }
          }
        }

        return false;
      };

      const handleDragStep = (node: ApNode) => {
        const attachmentHandled = handleDragToAttachStep(node);

        if (!attachmentHandled) {
          applyOperation(
            {
              type: FlowOperationType.DRAG_STEP,
              request: {
                stepName: node.id,
                newPosition: node.position,
              },
            },
            () => console.error('Operation failed')
          );
        }
      };

      const updateFlowEndNodePosition = (node: ApNewStepNode) => {
        setNodes((prevNodes) =>
          prevNodes.map((n) => {
            if (n.id === node.id) {
              const parentNode = prevNodes.find(
                (w) => w.id === node.data.parentStepName
              );
              if (parentNode) {
                return {
                  ...n,
                  position: {
                    x:
                      parentNode.position.x +
                      flowUtilConsts.AP_NODE_SIZE.STEP.width * 2,
                    y: parentNode.position.y,
                  },
                };
              }
            }
            return n;
          })
        );
      };

      const updateNewStepNodePosition = (node: ApNewStepNode) => {
        const parentStep = flowStructureUtil.getStepOrThrow(
          node.data.parentStepName,
          flowVersion
        );
        const newPos = flowCanvasUtils.getNewStepNodePosition(
          parentStep.name,
          parentStep.settings.position,
          flowVersion
        );
        const alignedNewStepPosition = {
          x:
            parentStep.settings.position.x +
            flowUtilConsts.AP_NODE_SIZE.STEP.width * 2,
          y: parentStep.settings.position.y,
        };
        setNodes((prevNodes) =>
          prevNodes.map((n) => {
            if (n.id === node.id && n.type === ApNodeType.NEW_STEP_NODE) {
              const newData: ApNewStepNode['data'] = {
                ...n.data,
                newStepPosition: alignedNewStepPosition,
                parentStepName: (n.data as ApNewStepNode['data'])
                  .parentStepName,
              };
              return {
                ...n,
                position: newPos,
                data: newData,
              } as ApNewStepNode;
            }
            return n;
          })
        );
      };

      if (draggedNode.type === ApNodeType.NEW_STEP_NODE) {
        handleNewStepNodeDragStop(draggedNode);
      } else {
        handleDragStep(draggedNode);
      }
    },
    [getIntersectingNodes, applyOperation, flowVersion]
  );

  const onNodeDrag = useCallback(
    (_event: React.MouseEvent, draggedNode: ApNode) => {
      const draggedStep =
        draggedNode.type === ApNodeType.STEP
          ? (draggedNode as ApStepNode).data.step
          : undefined;
      let targets: string[] = [];
      if (draggedStep) {
        targets =
          draggedStep.type === ActionType.LOOP_ON_ITEMS
            ? flowStructureUtil.getExternalLoopTargets(
                flowVersion,
                draggedStep.name
              )
            : flowStructureUtil.getStepTargets(flowVersion, draggedStep.name);
      }

      const updateAssociatedNodes = () => {
        setNodes((prevNodes) =>
          prevNodes.map((node) => {
            if (
              node.id === `${draggedNode.id}${BIG_ADD_BUTTON_NODE_SUFFIX}` &&
              node.type === ApNodeType.BIG_ADD_BUTTON
            ) {
              const position = {
                x:
                  draggedNode.position.x +
                  flowUtilConsts.AP_NODE_SIZE.STEP.width * 2,
                y:
                  draggedNode.position.y +
                  flowUtilConsts.AP_NODE_SIZE.STEP.width * 2,
              };
              return {
                ...node,
                position,
                data: { ...node.data, newStepPosition: position },
              } as ApBigAddButtonNode;
            }

            if (
              node.id === `${draggedNode.id}${LOOP_RETURN_NODE_SUFFIX}` &&
              node.type === ApNodeType.LOOP_RETURN_NODE
            ) {
              const position = {
                x:
                  draggedNode.position.x +
                  flowUtilConsts.AP_NODE_SIZE.STEP.width * 2.5,
                y:
                  draggedNode.position.y +
                  flowUtilConsts.AP_NODE_SIZE.STEP.height * 4.5,
              };
              return {
                ...node,
                position,
                data: { ...node.data },
              } as ApLoopReturnNode;
            }

            if (
              draggedStep?.type === ActionType.ROUTER &&
              node.id.startsWith(`${draggedNode.id}-`) &&
              node.id.endsWith(BIG_ADD_BUTTON_NODE_SUFFIX)
            ) {
              const match = node.id.match(/-(\d+)-big-add-button$/);
              if (match) {
                const branchIndex = Number(match[1]);
                const offsetX = flowUtilConsts.AP_NODE_SIZE.STEP.width * 4;
                const offsetY =
                  flowUtilConsts.AP_NODE_SIZE.STEP.width * 1.5 * branchIndex;
                return {
                  ...node,
                  position: {
                    x: draggedNode.position.x + offsetX,
                    y: draggedNode.position.y + offsetY,
                  },
                };
              }
            }

            return node;
          })
        );
      };

      const updateFlowEndNode = () => {
        setNodes((prevNodes) =>
          prevNodes.map((node) => {
            if (
              node.id === `${draggedNode.id}${FLOW_END_NODE_SUFFIX}` &&
              node.type === ApNodeType.NEW_STEP_NODE
            ) {
              const position = {
                x:
                  draggedNode.position.x +
                  flowUtilConsts.AP_NODE_SIZE.STEP.width * 2,
                y: draggedNode.position.y,
              };
              const newStepPosition = {
                x:
                  draggedNode.position.x +
                  flowUtilConsts.AP_NODE_SIZE.STEP.width * 2,
                y: draggedNode.position.y,
              };
              return {
                ...node,
                position,
                data: { ...node.data, newStepPosition: newStepPosition },
              } as ApNewStepNode;
            }
            return node;
          })
        );
      };

      const updateNewStepNode = () => {
        setNodes((prevNodes) =>
          prevNodes.map((node) => {
            if (
              node.id === `${draggedNode.id}${NEW_STEP_NODE_SUFFIX}` &&
              node.type === ApNodeType.NEW_STEP_NODE
            ) {
              const parentStep = flowStructureUtil.getStepOrThrow(
                node.data?.parentStepName ?? '',
                flowVersion
              );
              const position = flowCanvasUtils.getNewStepNodePosition(
                parentStep.name,
                draggedNode.position,
                flowVersion
              );
              const alignedNewStepPosition = {
                x:
                  draggedNode.position.x +
                  flowUtilConsts.AP_NODE_SIZE.STEP.width * 2,
                y: draggedNode.position.y,
              };
              return {
                ...node,
                position,
                data: {
                  ...(node.data as ApNewStepNode['data']),
                  newStepPosition: alignedNewStepPosition,
                },
              } as ApNewStepNode;
            }
            return node;
          })
        );
      };

      updateAssociatedNodes();

      if (targets.length === 0) {
        updateFlowEndNode();
      } else if (draggedNode.id === selectedStep) {
        updateNewStepNode();
      }
    },
    [flowVersion, selectedStep]
  );

  function updateEdgeHoverState<T extends ApEdge>(edge: T, hover: boolean): T {
    return {
      ...edge,
      data: {
        ...edge.data,
        hover,
      } as T['data'],
    };
  }

  function updateEdgeSelectedState<T extends ApEdge>(
    edge: T,
    selected: boolean
  ): T {
    return {
      ...edge,
      data: {
        ...edge.data,
        selected,
      } as T['data'],
    };
  }

  function updateEdgeState<T extends ApEdge>(edge: T): T {
    return {
      ...edge,
      data: {
        ...edge.data,
        selected: false,
        hover: false,
      } as T['data'],
    };
  }

  const onEdgeMouseEnter = useCallback((event: any, hoveredEdge: ApEdge) => {
    setEdges((prevEdges) =>
      prevEdges.map((edge) =>
        edge.id === hoveredEdge.id ? updateEdgeHoverState(edge, true) : edge
      )
    );
  }, []);

  const onEdgeMouseLeave = useCallback((event: any, hoveredEdge: ApEdge) => {
    if (hoveredEdge.data?.selected) {
      return;
    }
    setEdges((prevEdges) =>
      prevEdges.map((edge) =>
        edge.id === hoveredEdge.id ? updateEdgeHoverState(edge, false) : edge
      )
    );
  }, []);

  const onEdgeClick = useCallback((event: any, hoveredEdge: ApEdge) => {
    setEdges((prevEdges) =>
      prevEdges.map((edge) =>
        edge.id === hoveredEdge.id ? updateEdgeSelectedState(edge, true) : edge
      )
    );
  }, []);

  const [contextMenuType, setContextMenuType] = useState<ContextMenuType>(
    ContextMenuType.CANVAS
  );
  const onContextMenu = useCallback(
    (ev: React.MouseEvent<HTMLDivElement>) => {
      fetchClipboardOperations();
      if (ev.target instanceof HTMLElement || ev.target instanceof SVGElement) {
        const stepElement = ev.target.closest(
          `[data-${STEP_CONTEXT_MENU_ATTRIBUTE}]`
        );
        const stepName = stepElement?.getAttribute(
          `data-${STEP_CONTEXT_MENU_ATTRIBUTE}`
        );

        if (stepElement && stepName) {
          selectStepByName(stepName);
          storeApi.getState().addSelectedNodes([stepName]);
        }
        const targetIsSelectionChevron = ev.target.closest(
          `[data-${SELECTION_RECT_CHEVRON_ATTRIBUTE}]`
        );
        const targetIsSelectionRect = ev.target.classList.contains(
          NODE_SELECTION_RECT_CLASS_NAME
        );
        if (stepElement || targetIsSelectionRect || targetIsSelectionChevron) {
          setContextMenuType(ContextMenuType.STEP);
        } else {
          setContextMenuType(ContextMenuType.CANVAS);
        }
        if (
          doesSelectionRectangleExist() &&
          !targetIsSelectionRect &&
          !targetIsSelectionChevron
        ) {
          document
            .querySelector(`.${NODE_SELECTION_RECT_CLASS_NAME}`)
            ?.remove();
        }
      }
    },
    [setSelectedNodes, selectedNodes, doesSelectionRectangleExist]
  );

  useEffect(() => {
    setNodes((prevNodes) =>
      prevNodes.map((node) => {
        if (node.type === ApNodeType.NEW_STEP_NODE) {
          if (node.id.endsWith(FLOW_END_NODE_SUFFIX)) {
            return { ...node, hidden: false };
          }
          return {
            ...node,
            hidden: node.data?.parentStepName !== selectedStep,
          };
        }
        return node;
      })
    );

    setEdges((prevEdges) =>
      prevEdges.map((edge) => {
        const targetNodeId = edge.target;
        const isNewStepNode = targetNodeId.includes(NEW_STEP_NODE_SUFFIX);

        if (isNewStepNode) {
          if (targetNodeId.endsWith(FLOW_END_NODE_SUFFIX)) {
            return { ...edge, hidden: false };
          }
          const parentName = targetNodeId.replace(NEW_STEP_NODE_SUFFIX, '');
          return {
            ...edge,
            hidden: parentName !== selectedStep,
          };
        }
        return edge;
      })
    );
  }, [selectedStep]);

  return (
    <div ref={containerRef} className="size-full relative overflow-hidden z-50">
      <CanvasContextMenu
        selectedNodes={selectedNodes}
        applyOperation={applyOperation}
        selectedStep={selectedStep}
        exitStepSettings={exitStepSettings}
        flowVersion={flowVersion}
        readonly={readonly}
        setPieceSelectorStep={setPieceSelectorStep}
        actionsToPaste={actionsToPaste}
        contextMenuType={contextMenuType}
      >
        <ReactFlow
          onContextMenu={onContextMenu}
          onPaneClick={() => {
            storeApi.getState().unselectNodesAndEdges();
            setEdges((prevEdges) =>
              prevEdges.map((edge) => updateEdgeState(edge))
            );
          }}
          nodeTypes={flowUtilConsts.nodeTypes}
          nodes={nodes}
          edgeTypes={flowUtilConsts.edgeTypes}
          edges={edges}
          draggable={false}
          maxZoom={1.5}
          minZoom={0.5}
          panOnDrag={inGrabPanningMode ? [0, 1] : [1]}
          zoomOnDoubleClick={false}
          panOnScroll={true}
          panOnScrollMode={PanOnScrollMode.Free}
          fitView={false}
          nodesConnectable={true}
          elementsSelectable={true}
          onEdgeMouseEnter={onEdgeMouseEnter}
          onEdgeMouseLeave={onEdgeMouseLeave}
          onEdgeClick={onEdgeClick}
          nodesDraggable={false}
          nodesFocusable={false}
          onNodesChange={onNodesChange}
          onNodeDrag={onNodeDrag}
          onNodeDragStop={onNodeDragStop}
          multiSelectionKeyCode={inGrabPanningMode ? 'Shift' : null}
        ></ReactFlow>
      </CanvasContextMenu>
    </div>
  );
});

FlowCanvas.displayName = 'FlowCanvas';
